import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TwilioEnvVar, TwilioHealth } from '@/api/endpoints/platform';
import { VFStatCard } from '@/components/vf/StatCard';
import { currentLocale } from '@/i18n/locale';
import { TWILIO_ENV_VAR_ORDER } from './twilioEnv';

/**
 * « Opérateurs » tab of TwilioConfig (W6): the Twilio account, its balance,
 * the TwiML App of the dialer and the server's credentials (present or not,
 * never their value). Asterisk is chosen in « Opérateur des appels ».
 * Infrastructure stays out of the UI: no hosting console link.
 */
function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[12rem_1fr] sm:gap-3">
      <dt className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--vf-text-3)' }}>{label}</dt>
      <dd className="min-w-0 break-all text-sm">{children}</dd>
    </div>
  );
}

export function TwilioOperatorsTab({ health, isError }: { health?: TwilioHealth; isError: boolean }) {
  const { t } = useTranslation(['admin', 'common']);
  const state = isError ? 'error' : health ? 'ready' : 'loading';
  const account = health?.account.checked && health.account.reachable ? health.account : null;
  const app = health?.twiml_app.checked && health.twiml_app.found ? health.twiml_app : null;
  const dash = '—';
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <VFStatCard label={t('admin:twilioConfig.operators.account')} state={state} value={health ? (account ? account.friendly_name || dash : t('admin:twilioConfig.operators.unreachable')) : undefined} />
        <VFStatCard label={t('admin:twilioConfig.operators.accountStatus')} state={state} value={health ? (account ? account.status : dash) : undefined} />
        <VFStatCard
          label={t('admin:twilioConfig.balance.title')}
          state={health && !health.balance && account ? 'error' : state}
          value={health ? (health.balance ? `${Number(health.balance.amount).toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${health.balance.currency}` : dash) : undefined}
        />
      </div>

      <section aria-labelledby="twilio-app-title" className="rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
        <h2 id="twilio-app-title" className="text-base font-semibold">{t('admin:twilioConfig.twiml.detailsTitle')}</h2>
        <dl className="divide-y" style={{ borderColor: 'var(--vf-border)' }}>
          <Row label={t('admin:twilioConfig.operators.appName')}>{app?.friendly_name || dash}</Row>
          <Row label="SID"><span className="font-mono text-xs">{app?.sid || dash}</span></Row>
          <Row label={t('admin:twilioConfig.twiml.currentVoiceUrl')}><span className="font-mono text-xs">{app?.voice_url || t('admin:twilioConfig.twiml.notConfigured')}</span></Row>
          <Row label={t('admin:twilioConfig.operators.expectedVoiceUrl')}><span className="font-mono text-xs">{health?.expected_voice_url ?? dash}</span></Row>
          <Row label={t('admin:twilioConfig.operators.voiceMethod')}>{app?.voice_method || dash}</Row>
          {app?.status_callback && <Row label={t('admin:twilioConfig.operators.statusCallback')}><span className="font-mono text-xs">{app.status_callback}</span></Row>}
        </dl>
      </section>

      <section aria-labelledby="twilio-creds-title" className="space-y-2 rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
        <h2 id="twilio-creds-title" className="text-base font-semibold">{t('admin:twilioConfig.credentials.title')}</h2>
        <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.credentials.where')}</p>
        <div className="overflow-x-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
          <table className="w-full text-[13px]">
            <caption className="sr-only">{t('admin:twilioConfig.credentials.title')}</caption>
            <thead style={{ background: 'var(--vf-surface-1)' }}>
              <tr>
                <th scope="col" className="h-9 px-3 text-left text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vf-text-3)' }}>{t('admin:twilioConfig.credentials.variable')}</th>
                <th scope="col" className="h-9 px-3 text-left text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vf-text-3)' }}>{t('admin:twilioConfig.credentials.use')}</th>
                <th scope="col" className="h-9 px-3 text-left text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vf-text-3)' }}>{t('admin:twilioConfig.credentials.state')}</th>
              </tr>
            </thead>
            <tbody>
              {TWILIO_ENV_VAR_ORDER.map((key: TwilioEnvVar) => {
                const set = health?.credentials[key];
                return (
                  <tr key={key} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                    <td className="h-10 px-3 font-mono text-xs">{key}</td>
                    <td className="h-10 px-3">{t(`admin:twilioConfig.credentials.uses.${key}`)}</td>
                    <td className="h-10 px-3" style={{ color: set === undefined ? 'var(--vf-text-3)' : set ? 'var(--vf-fill-success)' : 'var(--vf-fill-danger)' }}>
                      {set === undefined ? dash : set ? t('admin:twilioConfig.credentials.statusOk') : t('admin:twilioConfig.credentials.statusMissing')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>
        {t('admin:twilioConfig.operators.otherCarriers')}{' '}
        <Link to="/AdminCallingProvider" className="underline" style={{ color: 'var(--vf-accent-text)' }}>{t('admin:superAdminLayout.nav.callingProvider')}</Link>
      </p>
    </div>
  );
}
